import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../sections/HeroSection';
import TrustBar from '../sections/TrustBar';
import ServicesOverview from '../sections/ServicesOverview';
import AIAnswerBlock from '../sections/AIAnswerBlock';
import ProcessSection from '../sections/ProcessSection';
import WhyChooseUs from '../sections/WhyChooseUs';
import ServiceAreas from '../sections/ServiceAreas';
import TestimonialsSection from '../sections/TestimonialsSection';
import FAQSection from '../sections/FAQSection';
import FinalCTA from '../sections/FinalCTA';
import type { HeroContent } from '../sections/HeroSection';
import type { TrustStat } from '../sections/TrustBar';
import type { ServicesContent } from '../sections/ServicesOverview';
import type { AIAnswerContent } from '../sections/AIAnswerBlock';
import type { ProcessContent } from '../sections/ProcessSection';
import type { WhyChooseUsContent } from '../sections/WhyChooseUs';
import type { ServiceAreasContent } from '../sections/ServiceAreas';
import type { TestimonialsContent } from '../sections/TestimonialsSection';
import type { FAQContent } from '../sections/FAQSection';
import type { FinalCTAContent } from '../sections/FinalCTA';

/* ─── Lead Form ─────────────────────────────────────────────────── */
const LeadForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === 'string') data.append(key, value);
    });
    await fetch('https://readdy.ai/api/form/d717o4muimmp87kg1bp0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    });
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-10 text-center border border-cream-dark">
        <div className="w-16 h-16 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-5">
          <i className="ri-checkbox-circle-fill text-gold text-3xl" />
        </div>
        <h3 className="font-serif text-navy text-2xl font-bold mb-3">Request Received!</h3>
        <p className="text-navy/55 text-sm font-sans leading-relaxed">
          Thank you for contacting Drain and Sewer Pro. Our team will reach out shortly to confirm your free estimate. You can also email us directly at Drainandsewerpro@gmail.com.
        </p>
      </div>
    );
  }

  return (
    <form
      id="quote-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl p-8 border border-cream-dark"
    >
      <h3 className="font-serif text-navy text-2xl font-bold mb-2">Request Free Estimate</h3>
      <p className="text-navy/45 text-sm font-sans mb-7">
        Response within 1 business hour. Or email us at{' '}
        <a href="mailto:Drainandsewerpro@gmail.com" className="text-gold hover:underline cursor-pointer">
          Drainandsewerpro@gmail.com
        </a>
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-navy/65 text-xs font-sans font-medium mb-1.5 tracking-wide uppercase">
              First Name *
            </label>
            <input
              name="first_name"
              type="text"
              required
              placeholder="John"
              className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm text-navy font-sans focus:outline-none focus:border-gold transition-colors bg-cream/50"
            />
          </div>
          <div>
            <label className="block text-navy/65 text-xs font-sans font-medium mb-1.5 tracking-wide uppercase">
              Last Name *
            </label>
            <input
              name="last_name"
              type="text"
              required
              placeholder="Smith"
              className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm text-navy font-sans focus:outline-none focus:border-gold transition-colors bg-cream/50"
            />
          </div>
        </div>

        <div>
          <label className="block text-navy/65 text-xs font-sans font-medium mb-1.5 tracking-wide uppercase">
            Email Address *
          </label>
          <input
            name="email"
            type="email"
            required
            placeholder="john@example.com"
            className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm text-navy font-sans focus:outline-none focus:border-gold transition-colors bg-cream/50"
          />
        </div>

        <div>
          <label className="block text-navy/65 text-xs font-sans font-medium mb-1.5 tracking-wide uppercase">
            Phone Number
          </label>
          <input
            name="phone"
            type="tel"
            placeholder="(909) 000-0000"
            className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm text-navy font-sans focus:outline-none focus:border-gold transition-colors bg-cream/50"
          />
        </div>

        <div>
          <label className="block text-navy/65 text-xs font-sans font-medium mb-1.5 tracking-wide uppercase">
            City / Location *
          </label>
          <input
            name="city"
            type="text"
            required
            placeholder="San Bernardino, CA"
            className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm text-navy font-sans focus:outline-none focus:border-gold transition-colors bg-cream/50"
          />
        </div>

        <div>
          <label className="block text-navy/65 text-xs font-sans font-medium mb-1.5 tracking-wide uppercase">
            Service Needed *
          </label>
          <select
            name="service"
            required
            className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm text-navy font-sans focus:outline-none focus:border-gold transition-colors bg-cream/50"
          >
            <option value="">Select a service...</option>
            <option value="Drain Cleaning">Drain Cleaning</option>
            <option value="Sewer Line Repair">Sewer Line Repair</option>
            <option value="Hydro Jetting">Hydro Jetting</option>
            <option value="Rooter Service">Rooter Service</option>
            <option value="Sewer Liners (CIPP)">Sewer Liners (CIPP)</option>
            <option value="Plumbing Repairs">Plumbing Repairs</option>
            <option value="Tankless Water Heaters">Tankless Water Heaters</option>
            <option value="Commercial Plumbing">Commercial Plumbing</option>
            <option value="Water Heater Service">Water Heater Service</option>
            <option value="Emergency Service">Emergency Service</option>
            <option value="Other / Not Sure">Other / Not Sure</option>
          </select>
        </div>

        <div>
          <label className="block text-navy/65 text-xs font-sans font-medium mb-1.5 tracking-wide uppercase">
            Describe the Issue
          </label>
          <textarea
            name="message"
            rows={4}
            maxLength={500}
            placeholder="Tell us what's happening — slow drain, sewer backup, water heater issue, emergency, etc."
            className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm text-navy font-sans focus:outline-none focus:border-gold transition-colors bg-cream/50 resize-none"
            onChange={e => setCharCount(e.target.value.length)}
          />
          <p className="text-right text-xs text-navy/30 mt-1 font-sans">{charCount}/500</p>
        </div>

        <button
          type="submit"
          className="w-full bg-gold hover:bg-gold-dark text-white font-semibold text-sm py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 group"
        >
          Send Free Estimate Request
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
        </button>

        <p className="text-center text-navy/30 text-xs font-sans">
          No spam. No pressure. Free estimate, always. Or email: Drainandsewerpro@gmail.com
        </p>
      </div>
    </form>
  );
};

/* ─── Page Content ───────────────────────────────────────────────── */
const heroContent: HeroContent = {
  eyebrow: 'Serving San Bernardino, CA & the Inland Empire',
  headlineLines: [
    "San Bernardino's",
    'Premier Drain &',
    'Sewer Experts.',
  ],
  subheadline:
    'Trusted local drain and sewer specialists based at 4892 North Pershing Avenue, San Bernardino, CA. Fast response times, free estimates, and guaranteed workmanship across all Inland Empire communities.',
  primaryCTA: { text: 'Get Free Estimate', link: '#quote' },
  secondaryCTA: { text: '24/7 Emergency Service', href: 'tel:+1-909-000-0000' },
  badges: [
    { icon: 'ri-map-pin-2-fill', label: 'San Bernardino Based' },
    { icon: 'ri-alarm-warning-line', label: '24/7 Response' },
    { icon: 'ri-shield-check-line', label: 'CA Licensed' },
    { icon: 'ri-star-fill', label: '4.9 Google Rating' },
    { icon: 'ri-timer-line', label: 'Same-Day Service' },
  ],
};

const trustStats: TrustStat[] = [
  { value: '10+', label: 'Years Serving San Bernardino', icon: 'ri-map-pin-2-fill', note: 'Inland Empire trusted' },
  { value: '500+', label: 'Local 5-Star Reviews', icon: 'ri-star-fill', note: '4.9 avg on Google' },
  { value: '<2hr', label: 'Avg Emergency Response', icon: 'ri-timer-line', note: 'Across San Bernardino' },
  { value: '11+', label: 'Cities Served', icon: 'ri-building-2-line', note: 'Throughout IE' },
];

const servicesContent: ServicesContent = {
  eyebrow: 'Local Services',
  headline: 'All 9 Services Available in San Bernardino',
  subheadline: 'Every drain, sewer, and water heater service — same day for most jobs.',
  services: [
    {
      icon: 'ri-water-flash-line',
      title: 'Drain Cleaning',
      bullets: [
        'Residential & commercial drain clearing',
        'Kitchen, bath & floor drains',
        'Same-day San Bernardino service',
      ],
      link: '/services',
    },
    {
      icon: 'ri-git-branch-line',
      title: 'Sewer Line Repair',
      bullets: [
        'Sewer diagnosis, repair & replacement',
        'Trenchless CIPP lining available',
        'Local San Bernardino expertise',
      ],
      link: '/services',
    },
    {
      icon: 'ri-alarm-warning-line',
      title: '24/7 Emergency Service',
      bullets: [
        'Immediate emergency dispatch',
        'Sewer backups & flooding',
        'Sub-90-minute local response',
      ],
      link: '/services',
    },
  ],
  ctaText: 'View All 9 Services',
  ctaLink: '/services',
};

const aiContent: AIAnswerContent = {
  mainQuestion: 'Why Choose Drain and Sewer Pro in San Bernardino, CA?',
  answer:
    'Drain and Sewer Pro is San Bernardino\'s locally owned drain and sewer company located at 4892 North Pershing Avenue, San Bernardino, CA 92407. Contact: Drainandsewerpro@gmail.com. With 10+ years of Inland Empire service, we offer same-day drain cleaning, sewer repair, hydro jetting, tankless water heater installation, commercial plumbing, and 24/7 emergency plumbing — all by CA-licensed technicians with transparent pricing and guaranteed results.',
  leftBlock: {
    title: 'Local Service Areas',
    items: [
      'San Bernardino (All neighborhoods)',
      'Riverside & Moreno Valley',
      'Fontana & Rialto',
      'Redlands & Highland',
      'Colton & Loma Linda',
      'Upland & Rancho Cucamonga',
    ],
  },
  rightBlock: {
    title: 'Why We Know San Bernardino',
    items: [
      'Local soil & pipe condition expertise',
      'San Bernardino building code knowledge',
      'Local inspector relationships',
      'Fast local emergency response',
      'Community-invested local ownership',
      '10+ years of Inland Empire service',
    ],
  },
};

const processContent: ProcessContent = {
  eyebrow: 'How We Serve San Bernardino',
  headline: 'Local Service, Done Right',
  steps: [
    {
      number: '01',
      title: 'Fast Local Response',
      description: 'Based in San Bernardino — shorter drive times mean faster arrival for your drain or sewer emergency.',
    },
    {
      number: '02',
      title: 'Local Expertise',
      description: 'Years of San Bernardino work means we know local pipe conditions, soil types, and code requirements.',
    },
    {
      number: '03',
      title: 'Quality Workmanship',
      description: 'Every job is completed to CA code standards by licensed technicians using professional equipment.',
    },
    {
      number: '04',
      title: 'Community Care',
      description: "We're your neighbors at 4892 N. Pershing Ave. We treat every property as if it were our own.",
    },
  ],
};

const whyContent: WhyChooseUsContent = {
  subheadline: 'Local Advantage',
  headline: "Why San Bernardino Trusts Drain & Sewer Pro",
  body:
    'Being local means more than a shorter drive. It means 10+ years of built relationships in this community. Our reputation depends on the quality of every drain cleaning and sewer repair we perform across San Bernardino and surrounding cities. We take that seriously.',
  ctaText: 'Request Free Estimate',
  ctaLink: '#quote',
  features: [
    {
      icon: 'ri-timer-line',
      title: 'Fast San Bernardino Response',
      description: 'Based locally, we reach most San Bernardino locations within 60 minutes for emergency calls.',
    },
    {
      icon: 'ri-book-2-line',
      title: 'Know Local Codes',
      description: 'Fully versed in San Bernardino City and San Bernardino County plumbing codes.',
    },
    {
      icon: 'ri-group-line',
      title: 'Community Invested',
      description: 'We live in the neighborhoods we serve. Your community matters to us personally.',
    },
    {
      icon: 'ri-message-3-line',
      title: '500+ Local Reviews',
      description: 'Verified reviews from real San Bernardino and Inland Empire customers on Google.',
    },
    {
      icon: 'ri-map-2-line',
      title: 'Local Infrastructure Experts',
      description: 'From older Craftsman-era pipes to new construction sewer systems — we know local infrastructure.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'San Bernardino Guaranteed',
      description: 'Written guarantee on every job. Your satisfaction is our local reputation on the line.',
    },
  ],
};

const areasContent: ServiceAreasContent = {
  eyebrow: 'Coverage Map',
  headline: 'Drain & Sewer Service Throughout the Inland Empire',
  description: 'Drain and Sewer Pro provides comprehensive drain and sewer coverage throughout San Bernardino and surrounding Inland Empire communities.',
  areas: [
    'San Bernardino',
    'Riverside',
    'Fontana',
    'Moreno Valley',
    'Rancho Cucamonga',
    'Rialto',
    'Redlands',
    'Highland',
    'Colton',
    'Loma Linda',
    'Upland',
  ],
  note: 'Emergency drain and sewer service available 24/7 across all service areas. Call to confirm same-day availability.',
  mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52833.65!2d-117.2882!3d34.1538!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c35c9a6d000001%3A0x1!2s4892+N+Pershing+Ave%2C+San+Bernardino%2C+CA+92407!5e0!3m2!1sen!2sus!4v1711000000001!5m2!1sen!2sus',
};

const testimonialsContent: TestimonialsContent = {
  eyebrow: 'San Bernardino Reviews',
  headline: 'San Bernardino Client Reviews',
  overallRating: '4.9',
  reviewCount: '500+',
  testimonials: [
    {
      text: 'Our main sewer backed up on a Saturday night. Drain and Sewer Pro had a licensed technician at our San Bernardino home within 90 minutes. Fast, thorough, professional, and fairly priced. I could not ask for better local service.',
      name: 'Robert A.',
      location: 'San Bernardino, CA',
      rating: 5,
    },
    {
      text: 'I manage several properties in the Highland area. Drain and Sewer Pro is my exclusive call for any drain or sewer issue. Response time, quality, and pricing are consistently excellent every single time.',
      name: 'Diana F.',
      location: 'Highland, CA',
      rating: 5,
    },
    {
      text: 'Finally found a local plumber I completely trust. They used a camera to find a root intrusion in our sewer line, showed us exactly what was happening, and fixed it same day. Drain and Sewer Pro is absolutely the real deal.',
      name: 'Tom & Karen S.',
      location: 'Loma Linda, CA',
      rating: 5,
    },
  ],
};

const faqContent: FAQContent = {
  eyebrow: 'Local FAQs',
  headline: 'San Bernardino Service Questions',
  faqs: [
    {
      question: 'Do you serve all neighborhoods in San Bernardino, CA?',
      answer:
        'Yes. Drain and Sewer Pro serves all San Bernardino neighborhoods including Arrowhead, Del Rosa, Muscoy, and Verdemont. We also cover the broader San Bernardino County region. Call or email Drainandsewerpro@gmail.com to confirm same-day drain or sewer availability at your specific address.',
    },
    {
      question: 'How fast can you reach San Bernardino for an emergency?',
      answer:
        'Being locally based at 4892 N. Pershing Ave, our average emergency response is under 90 minutes for most San Bernardino locations. Standard same-day appointments are available with morning and afternoon windows. Emergency calls are dispatched immediately, day or night.',
    },
    {
      question: 'What is the cost of drain cleaning in San Bernardino?',
      answer:
        'Drain cleaning costs depend on drain type, blockage severity, and method required. Drain and Sewer Pro provides completely free on-site estimates for all jobs — with transparent written quotes before any work begins. No hidden fees or surprise charges on your final invoice.',
    },
    {
      question: 'Do you know San Bernardino city plumbing codes?',
      answer:
        'Absolutely. Our technicians are fully trained in San Bernardino City and San Bernardino County plumbing codes. We handle all required permits and inspections when applicable, ensuring every drain and sewer repair meets local compliance standards and protects your property value.',
    },
    {
      question: 'Are you licensed to work in San Bernardino, CA?',
      answer:
        'Yes. Drain and Sewer Pro holds a valid California State Contractor License and all local business licenses to perform plumbing and sewer work in San Bernardino and throughout San Bernardino County. All technicians are individually licensed, insured, and background-checked.',
    },
    {
      question: 'Do you offer 24/7 emergency drain service in San Bernardino?',
      answer:
        'Yes. 24/7 emergency drain and sewer service is available to all San Bernardino locations at any hour. Whether a main sewer backup, burst pipe, or severely blocked drain at midnight or on a holiday — call Drain and Sewer Pro and we dispatch a licensed technician immediately.',
    },
  ],
};

const finalCTAContent: FinalCTAContent = {
  eyebrow: "San Bernardino's #1 Drain & Sewer Team",
  headlineLines: ['Ready to Serve', 'San Bernardino.'],
  subheadline: 'Your local drain and sewer experts are standing by — free estimate, guaranteed results, and fast response across the Inland Empire.',
  primaryCTA: { text: 'Get Free Estimate', link: '#quote' },
  secondaryCTA: { text: '24/7 Emergency Service', href: 'tel:+1-909-000-0000' },
};

const LocationPage = () => (
  <div className="w-full">
    <Navbar />
    <main>
      <HeroSection content={heroContent} />
      <TrustBar stats={trustStats} />
      <ServicesOverview content={servicesContent} />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs content={whyContent} />
      <ServiceAreas content={areasContent} />
      <TestimonialsSection content={testimonialsContent} />
      <FAQSection content={faqContent} />

      {/* ── LEAD FORM SECTION ────────────────────────────────── */}
      <section id="quote" className="bg-cream py-28">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left info */}
            <div>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-gold" />
                <span className="text-gold text-xs tracking-[0.25em] uppercase font-sans font-medium">
                  Free Estimate
                </span>
              </div>
              <h2 className="font-serif text-navy font-bold text-4xl md:text-5xl leading-tight mb-6">
                Request Your Free Drain &amp; Sewer Estimate
              </h2>
              <div className="h-[3px] w-12 bg-gold mb-8" />
              <p className="text-navy/55 text-base leading-relaxed font-sans mb-10">
                Tell us about your drain or sewer issue and a Drain and Sewer Pro specialist will respond quickly with a transparent, no-obligation estimate. Serving San Bernardino, Riverside, Fontana, Moreno Valley, Rancho Cucamonga, and all Inland Empire communities.
              </p>

              {/* Contact details */}
              <div className="space-y-4 mb-10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-gold text-base" />
                  </div>
                  <span className="text-navy/65 text-sm font-sans">4892 North Pershing Avenue, San Bernardino, CA 92407</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-gold text-base" />
                  </div>
                  <a href="mailto:Drainandsewerpro@gmail.com" className="text-navy/65 text-sm font-sans hover:text-gold transition-colors cursor-pointer">
                    Drainandsewerpro@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-gold/10 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-gold text-base" />
                  </div>
                  <span className="text-navy/65 text-sm font-sans">24/7 Emergency Service Available</span>
                </div>
              </div>

              <ul className="space-y-4">
                {[
                  { icon: 'ri-checkbox-circle-fill', text: 'No obligation — 100% free estimate' },
                  { icon: 'ri-timer-line', text: 'Response within 1 hour during service hours' },
                  { icon: 'ri-shield-check-line', text: 'Licensed & insured technicians only' },
                  { icon: 'ri-price-tag-3-line', text: 'Transparent written quotes, no surprises' },
                ].map(item => (
                  <li key={item.text} className="flex items-center gap-3">
                    <i className={`${item.icon} text-gold text-lg flex-shrink-0`} />
                    <span className="text-navy/65 text-sm font-sans">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right form */}
            <div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      <FinalCTA content={finalCTAContent} />
    </main>
    <Footer />
  </div>
);

export default LocationPage;
